import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { fetchCustomers } from 'actions/action-filters';
import { fetchCustomerClinics } from 'actions/action-lookups';
import { Controller, useForm } from 'react-hook-form';
import { nameOfFactory } from 'utils/types-util';
import { ReactSelect } from 'components/form/field/react_select';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { NumberOption } from 'interfaces/forms/types';
import { UserPermissions } from 'interfaces/user/Permissions';
import { UserUtils } from 'utils/user-utils';

interface ICustomerDropdownProps {
  selectedCustomerId: number;
  setSelectedCustomerId: (customerId: number) => void;
  isLabelOn?: boolean;
  isSelectedOption?: boolean;
  requiredPermission?: UserPermissions;
}

interface IFormFields {
  customerId: {
    label: string;
    value: number;
  };
}

const getFormFields = nameOfFactory<IFormFields>();

export const CustomerDropdown: React.FC<ICustomerDropdownProps> = (
  props: ICustomerDropdownProps,
): JSX.Element => {
  const dispatch = useDispatch();
  const customers = useTypedSelector(state => state.filters.customers);

  const form = useForm<any>();

  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchCustomerClinics());
  }, [fetchCustomers, fetchCustomerClinics]);

  const allCustomers = useMemo(() => {
    const options = [...customers]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map<NumberOption>(x => {
        const isDisabled =
          props.requiredPermission != null &&
          !UserUtils.userIsPermitted(props.requiredPermission, x.id);
        return {
          label: isDisabled ? `${x.name} (No permission)` : x.name,
          value: x.id,
          disabled: isDisabled,
        };
      });

    if (props.isSelectedOption) {
      options.push({ label: 'Select Customer', value: 0 });
    }

    return options;
  }, [customers, props.requiredPermission, props.isSelectedOption]);

  return (
    <Controller
      defaultValue={
        allCustomers.find(customer => customer.value === props.selectedCustomerId) ||
        allCustomers[0]
      }
      control={form.control}
      name={getFormFields('customerId')}
      render={(ctrlProps: any) => {
        return (
          <ReactSelect
            value={ctrlProps?.field?.value}
            fields={allCustomers}
            isMulti={false}
            label={props.isLabelOn ? 'Customer' : undefined}
            isOptionDisabled={(option: NumberOption) => option.disabled === true}
            handleOnChange={(value: { label: string; value: number }) => {
              form.setValue(getFormFields('customerId'), value);
              props.setSelectedCustomerId(value.value);
            }}
          />
        );
      }}
    />
  );
};

export default CustomerDropdown;
